import CommercialCriteriaOrAccountNotConfigured from "@components/CommercialCriteria/CommercialCriteriaOrAccountNotConfigured";
import AddBillingContextModal from "@components/billingContext/AddBillingContextModel";
import BillingContextGrid from "@components/billingContext/BillingContextGrid";
import { BillingContext } from "@components/customer/BillingContext.types";
import useAccessType from "@hooks/accessType/useAccessType";
import useAccountConfig from "@hooks/accountConfig/useAccountConfig";
import useBillingContext from "@hooks/billingContext/useBillingContext";
import useCommercialCriteria from "@hooks/commercialCriteria/useCommercialCriteria";
import { SpinnerLoader } from "@ucchub/ui";
import Button from "@vfuk/core-button";
import Container from "@vfuk/core-container";
import Divider from "@vfuk/core-divider";
import LinkWithIcon from "@vfuk/core-link-with-icon";
import SimpleGrid from "@vfuk/core-simple-grid";
import Spacing from "@vfuk/core-spacing";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useCountry from "@ucchub/ui/src/cloudNumbers/hooks/country/useCountry";

type Props = {
  customerId: string;
};

const BillingContextTab = (props: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { fetchAccountConfig, isLoading, organisationInfo } =
    useAccountConfig();

  const {
    fetchCommercialCriteriaList,
    commercialCriteriaList,
    isCommercialCriteriaListLoading,
  } = useCommercialCriteria();

  const { fetchCountryList, isCountryLoading, countryList } = useCountry();
  const { fetchAccessTypeList, accessTypeList, isAccessTypeLoading } =
    useAccessType();
  const {
    fetchBillingContextList,
    billingContextList,
    isBillingContextLoading,
  } = useBillingContext();

  const loadData = () => {
    fetchBillingContextList(props.customerId);
    fetchAccountConfig(props.customerId);
    fetchCommercialCriteriaList(props.customerId);
    fetchCountryList();
    fetchAccessTypeList();
  };

  useEffect(() => {
    if (props.customerId) loadData();
  }, [props.customerId]);

  const [openAddBillingContextModal, setOpenAddBillingContextModal] =
    useState(false);
  const isCommercialCriteriaExist =
    commercialCriteriaList && commercialCriteriaList.length;

  const isBillingContextExist = billingContextList && billingContextList.length;

  const isAllAPILoading =
    isBillingContextLoading ||
    isLoading ||
    isCommercialCriteriaListLoading ||
    isCountryLoading ||
    isAccessTypeLoading;

  const customerLink = "/customers/" + props.customerId + "/view";

  return (
    <>
      {isAllAPILoading ? (
        <SpinnerLoader
          srText={
            t("core:billingContext.loading_billing_context") ||
            "Loading commercial criteria"
          }
          text={
            t("core:billingContext.loading_billing_context") ||
            "Loading commercial criteria"
          }
        />
      ) : (
        <>
          {isCommercialCriteriaExist ? (
            <>
              {isBillingContextExist ? (
                <>
                  {billingContextList.map(
                    (eachBillingContext: BillingContext, index: number) => (
                      <Spacing
                        spacingLevel={{ top: 3 }}
                        key={index + "spacing"}
                      >
                        <Container appearance="primary" elevation>
                          <BillingContextGrid
                            commercialId={organisationInfo?.commercialId || ""}
                            billingContext={eachBillingContext}
                            commercialCriteriaList={commercialCriteriaList}
                            key={index}
                            countryList={countryList}
                            accessTypeList={accessTypeList}
                          />
                        </Container>
                      </Spacing>
                    )
                  )}
                  <Spacing spacingLevel={{ top: 3 }}>
                    <Button
                      text={t(
                        "core:billingContext:add_another_billing_context"
                      )}
                      appearance="secondary"
                      onClick={() => setOpenAddBillingContextModal(true)}
                    />
                  </Spacing>
                </>
              ) : (
                <>
                  <CommercialCriteriaOrAccountNotConfigured
                    content={t("core:billingContext.no_billing_context")}
                  />
                  <SimpleGrid justify="center">
                    <Button
                      text={t("core:billingContext:add_billing_context")}
                      appearance="primary"
                      onClick={() => setOpenAddBillingContextModal(true)}
                    />
                  </SimpleGrid>
                </>
              )}
            </>
          ) : (
            <CommercialCriteriaOrAccountNotConfigured
              content={t("core:billingContext.account_warning")}
            />
          )}
          {props.customerId && (
            <AddBillingContextModal
              isOpen={openAddBillingContextModal}
              customerId={props.customerId}
              onClose={() => setOpenAddBillingContextModal(false)}
              commercialCriteria={commercialCriteriaList}
              countryList={countryList}
              accessTypeList={accessTypeList}
              refreshData={loadData}
              commercialId={organisationInfo?.commercialId || ""}
              billingContextList={billingContextList}
            />
          )}
        </>
      )}
      <Spacing spacingLevel={{ top: 5, bottom: 4 }}>
        <Divider appearance="secondary" />
      </Spacing>
      <Spacing spacingLevel={{ right: 2 }}>
        <LinkWithIcon
          appearance="secondary"
          text={t("core:billingContext:back_button")}
          icon={{
            name: "chevron-left",
            justify: "left",
          }}
          onClick={() => {
            navigate(customerLink);
          }}
          size={3}
        />
      </Spacing>
    </>
  );
};

export default BillingContextTab;
